import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import EstablishmentStorage from 'storage/establishment'
import { IEstablishmentResponse } from 'types'

/**
 * EstablishmentState interface
 * This interface defines the structure of the establishment state.
 */
interface EstablishmentState {
  /**
   * data property
   * This property holds the current establishment data.
   * It can be null or an IEstablishmentResponse object.
   */
  data?: IEstablishmentResponse | null
}

const storage = EstablishmentStorage.currentEstablishment.get()

/**
 * Function to get establishment data from storage
 */
const getEstablishmentData = () => {
  try {
    const raw = EstablishmentStorage.currentEstablishment.get()
    if (!raw) return null
    const establishment = JSON.parse(raw)
    delete establishment['commercial_name']
    return establishment
  } catch (error) {
    console.warn('Erro ao fazer parse do estabelecimento:', error)
    return null
  }
}

/**
 * Initial state for the establishment slice
 * It initializes the data property with the establishment data from storage.
 */
const initialState: EstablishmentState = {
  data: getEstablishmentData() as IEstablishmentResponse | null
}

/**
 * EstablishmentSlice
 * This slice manages the establishment state in the Redux store.
 * It provides actions to set the current establishment, set the initial establishment,
 * and clean the establishment data.
 */
const establishmentSlice = createSlice({
  name: 'establishment',
  initialState,
  reducers: {
    setCurrentEstablishment: (
      state,
      action: PayloadAction<IEstablishmentResponse | undefined>
    ) => {
      state.data = action.payload
      if (action.payload)
        EstablishmentStorage.currentEstablishment.set(
          JSON.stringify(action.payload)
        )
      else EstablishmentStorage.currentEstablishment.remove()
    },
    setInitialEstablishment: (
      state,
      action: PayloadAction<string | undefined>
    ) => {
      const establishment = {
        establishment_id: action.payload
      } as IEstablishmentResponse
      state.data = establishment
      EstablishmentStorage.currentEstablishment.set(
        JSON.stringify(establishment)
      )
    },
    cleanEstablishment: state => {
      state.data = {} as IEstablishmentResponse
      EstablishmentStorage.currentEstablishment.remove()
    }
  }
})

/**
 * Reducer for the establishment slice
 * This reducer handles the actions defined in the establishment slice.
 */
const establishmentReducer = establishmentSlice.reducer

export const {
  setCurrentEstablishment,
  setInitialEstablishment,
  cleanEstablishment
} = establishmentSlice.actions
export default establishmentReducer
